import React, { useEffect, useState } from 'react'
import { Pressable, View } from 'react-native'

import { useTranslation } from 'react-i18next'

import CloseIcon from '@common/assets/svg/CloseIcon'
import useTheme from '@common/hooks/useTheme'
import useBackgroundService from '@web/hooks/useBackgroundService'
import useRequestsControllerState from '@web/hooks/useRequestsControllerState'
import SignMessageScreen from '@web/modules/sign-message/screens/SignMessageScreen'
import useHomeBreakpoints from '@mobile/modules/dashboard/screens/home/useHomeBreakpoints'

// Off-chain message kinds that the @web SignMessageScreen knows how to render.
// Kept in sync with the same list inside SignMessageScreen / getInitialRoute.
const SIGN_MESSAGE_KINDS = ['message', 'typedMessage', 'siwe', 'authorization-7702']

// How long to keep the screen mounted after the request resolves — see below.
const UNMOUNT_GRACE_MS = 1500

/**
 * Mobile-web bridge for off-chain message signing.
 *
 * On the extension a typedMessage/message user request opens a *separate*
 * notification window with the Sign Message screen, so the originating tab (and
 * its hooks, e.g. useGaslessSwap / usePermitSwap watching for the signature)
 * stays mounted. The mobile-web SPA has a single window: route-navigating to
 * `/sign-message` would unmount the swap screen and kill the hook waiting for
 * the signature mid-flow. So instead of navigating, we render the very same
 * @web SignMessageScreen on top of the active screen — as a centered modal on
 * wide viewports and a bottom sheet on mobile (the design's "Sign message"
 * popup), rather than a fullscreen takeover.
 *
 * The grace window (below) is load-bearing. When the user signs, the controller
 * sets `signedMessage` and then immediately resolves the user request, which
 * clears `currentUserRequest`. If we unmounted SignMessageScreen the instant the
 * request cleared, its unmount effect would dispatch SIGN_MESSAGE_RESET and wipe
 * `signedMessage` in the same tick — the set+reset collapse in state-sync and
 * the requesting hook (gasless/permit, mounted on the screen underneath) never
 * observes the signature, so its flow stalls forever. Keeping the screen mounted
 * for a short grace period after the request clears suppresses that premature
 * reset, so the signature stays visible long enough to be consumed. The hook
 * itself dispatches SIGN_MESSAGE_RESET once it has read the signature.
 *
 * Mobile-web-only by construction — it's mounted solely from Router.web.
 */
const SignMessageOverlay = () => {
  const { currentUserRequest } = useRequestsControllerState()
  const { theme } = useTheme()
  const { t } = useTranslation()
  const { dispatch } = useBackgroundService()
  const { isMobile } = useHomeBreakpoints()

  const hasSignMessageRequest =
    !!currentUserRequest && SIGN_MESSAGE_KINDS.includes(currentUserRequest.kind)

  // Stays true through the grace window after the request clears, so the screen
  // doesn't unmount (and reset signedMessage) before the requesting hook reads it.
  const [keepMounted, setKeepMounted] = useState(false)

  useEffect(() => {
    if (hasSignMessageRequest) {
      setKeepMounted(true)
      return undefined
    }
    if (!keepMounted) return undefined
    const timer = setTimeout(() => setKeepMounted(false), UNMOUNT_GRACE_MS)
    return () => clearTimeout(timer)
  }, [hasSignMessageRequest, keepMounted])

  if (!hasSignMessageRequest && !keepMounted) return null

  // Dimmed backdrop with the modal panel above it. Desktop: centered dialog
  // (capped width/height). Mobile: bottom sheet pinned to the bottom edge with a
  // rounded top. The panel clips SignMessageScreen's own header/body/footer.
  const onClose = () => {
    if (!currentUserRequest) return
    dispatch({
      type: 'REQUESTS_CONTROLLER_REJECT_USER_REQUEST',
      params: { err: t('User rejected the request.'), id: currentUserRequest.id }
    })
  }

  const panelStyle = isMobile
    ? ({
        width: '100%',
        maxHeight: '94%',
        borderTopLeftRadius: 20,
        borderTopRightRadius: 20
      } as const)
    : ({
        width: '100%',
        maxWidth: 480,
        maxHeight: '88%',
        borderRadius: 16
      } as const)

  return (
    <View
      style={
        {
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          backgroundColor: theme.backdrop as string,
          // Web-only frosted backdrop behind the modal.
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: isMobile ? 'flex-end' : 'center',
          paddingHorizontal: isMobile ? 0 : 16
        } as any
      }
      pointerEvents={hasSignMessageRequest ? 'auto' : 'none'}
    >
      <View
        style={[
          {
            overflow: 'hidden',
            backgroundColor: theme.secondaryBackground as string,
            // Flex so the inner TabLayoutContainer (flex:1) sizes to the panel,
            // keeping the Reject/Sign footer pinned to the panel's bottom edge.
            flexShrink: 1
          },
          panelStyle
        ]}
      >
        <SignMessageScreen />
        <Pressable
          onPress={onClose}
          style={({ hovered }: any) => ({
            position: 'absolute',
            top: 20,
            right: 20,
            width: 30,
            height: 30,
            borderRadius: 15,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: hovered
              ? (theme.tertiaryBackground as string)
              : (theme.quinaryBackground as string),
            zIndex: 5
          })}
          testID="sign-message-close"
        >
          <CloseIcon width={13} height={13} color={theme.secondaryText as string} />
        </Pressable>
      </View>
    </View>
  )
}

export default SignMessageOverlay
